import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { ReactComponent as EditIcon } from '../../../assets/SVG/edit icon.svg';
import { ReactComponent as ViewIcon } from '../../../assets/SVG/eye_view.svg';
import StockStatusChip from '../../Inventory/ReusableComponents/StockStatusChip';
import ApprovalFlowCell from './ApprovalFlowCell';
import ApprovalToggleSwitch from './ApprovalToggleSwitch';
import TableSkeleton from '../../../components/common/TableSkeleton';
import { ApprovalRule } from './inventoryApproval.types';
import useCurrency from '../../../components/CurrencyProvider';
import {
  approvalTableContainerSx,
  approvalTableHeadRowSx,
  approvalTableHeaderCellSx,
  approvalTableBodyCellSx,
  APPROVAL_TABLE_COL_WIDTHS,
  APPROVAL_TABLE_SKELETON_WIDTHS,
} from './InventoryApprovalSx';

interface InventoryApprovalTableProps {
  rows: ApprovalRule[];
  activeStates: Record<number, boolean>;
  onToggle: (id: number) => void;
  onEdit: (id: number) => void;
  onView: (id: number) => void;
  isLoading?: boolean;
  isError?: boolean;
}

const InventoryApprovalTable: React.FC<InventoryApprovalTableProps> = ({ rows, activeStates, onToggle, onEdit, onView, isLoading = false, isError = false }) => {
  const { t: tInventoryApproval } = useTranslation('inventory_approval_config');
  const { currencySymbol, formatNumber } = useCurrency();

  return (
    <TableContainer
      component={Paper}
      sx={approvalTableContainerSx}
    >
      <Table>
        <TableHead>
          <TableRow sx={approvalTableHeadRowSx}>
            <TableCell sx={{ ...approvalTableHeaderCellSx, width: APPROVAL_TABLE_COL_WIDTHS.ruleName }}>{tInventoryApproval('rule_name')}</TableCell>
            <TableCell sx={{ ...approvalTableHeaderCellSx, width: APPROVAL_TABLE_COL_WIDTHS.documentType }}>{tInventoryApproval('document_type')}</TableCell>
            <TableCell sx={{ ...approvalTableHeaderCellSx, width: APPROVAL_TABLE_COL_WIDTHS.clinic }}>{tInventoryApproval('clinic')}</TableCell>
            <TableCell sx={{ ...approvalTableHeaderCellSx, width: APPROVAL_TABLE_COL_WIDTHS.conditions }}>{tInventoryApproval('conditions')}</TableCell>
            <TableCell sx={{ ...approvalTableHeaderCellSx, width: APPROVAL_TABLE_COL_WIDTHS.approvalFlow }}>{tInventoryApproval('approval_flow')}</TableCell>
            <TableCell sx={{ ...approvalTableHeaderCellSx, width: APPROVAL_TABLE_COL_WIDTHS.activeToggle, textAlign: 'center' }}>
              {tInventoryApproval('active_inactive')}
            </TableCell>
            <TableCell sx={{ ...approvalTableHeaderCellSx, width: APPROVAL_TABLE_COL_WIDTHS.actions, textAlign: 'center' }} />
          </TableRow>
        </TableHead>

        <TableBody>
          {isLoading ? (
            <TableSkeleton rows={8} columnWidths={APPROVAL_TABLE_SKELETON_WIDTHS} />
          ) : isError ? (
            <TableRow>
              <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="error">{tInventoryApproval('load_error')}</Typography>
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">{tInventoryApproval('no_rules')}</Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((rule) => (
              <TableRow key={rule.id} hover>
                <TableCell sx={approvalTableBodyCellSx}>{rule.name}</TableCell>
                <TableCell sx={approvalTableBodyCellSx}>
                  <StockStatusChip value={rule.documentType.code} label={rule.documentType.name} />
                </TableCell>
                <TableCell sx={approvalTableBodyCellSx}>{rule.clinic_name}</TableCell>
                <TableCell sx={approvalTableBodyCellSx}>
                  {rule.max_amount === null
                    ? `> ${currencySymbol}${formatNumber(rule.min_amount ?? 0)}`
                    : (rule.min_amount === null || rule.min_amount === 0)
                      ? `< ${currencySymbol}${formatNumber(rule.max_amount)}`
                      : `${currencySymbol}${formatNumber(rule.min_amount)} - ${currencySymbol}${formatNumber(rule.max_amount)}`}
                </TableCell>
                <TableCell sx={{ ...approvalTableBodyCellSx, width: 600, minWidth: 500, maxWidth: 600 }}>
                  <ApprovalFlowCell flow={rule.approvalFlow} />
                </TableCell>
                <TableCell sx={{ ...approvalTableBodyCellSx, textAlign: 'center' }}>
                  <ApprovalToggleSwitch
                    checked={activeStates[rule.id] ?? rule.status === 'ACTIVE'}
                    onChange={() => onToggle(rule.id)}
                  />
                </TableCell>
                <TableCell sx={{ ...approvalTableBodyCellSx, textAlign: 'center' , display: 'flex', justifyContent: 'center', gap: 1}}>
                  <Tooltip title={tInventoryApproval('view')} arrow>
                    <IconButton size="small" onClick={() => onView(rule.id)} data-testid={`approval-view-btn-${rule.id}`}>
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={tInventoryApproval('edit')} arrow>
                    <IconButton size="small" onClick={() => onEdit(rule.id)} data-testid={`approval-edit-btn-${rule.id}`}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default InventoryApprovalTable;
